import { FC, useCallback } from 'react';

import classnames from 'classnames';
import { getFormattedIntNumber } from 'utils/clickme';

import cpcQuickPublicationTypeButtonClick, {
    PublicationType,
} from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_menu/cpc_quick/cpc_quick_publication_type_button_click';
import { CheckableCard, Text, VSpacingContainer } from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';

import { MagritteFormField } from 'components/common/MagritteFormField/MagritteFormField';

import {
    QuickCampaignFieldName,
    QuickCampaignStartType,
    selectQuickCampaignConfig,
    selectQuickCampaignFormDataField,
    setQuickCampaignFieldByName,
} from 'store/models/createQuickCampaign';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/AdQuickCreate/CampaignSources/CampaignSources.module.less';

const TrlKeys = {
    tariffLabel: 'AdQuickCreate.CampaignSources.tariffLabel',
    [QuickCampaignStartType.Basic]: 'create.campaign.radio.sources.basic',
    [QuickCampaignStartType.Standard]: 'create.campaign.radio.sources.standard',
    [QuickCampaignStartType.Maximum]: 'create.campaign.radio.sources.maximum',
    budget: 'create.campaign.radio.sources.budget',
};

export const CampaignSources: FC = () => {
    const config = useSelector(selectQuickCampaignConfig);
    const quickStartType = useSelector(selectQuickCampaignFormDataField[QuickCampaignFieldName.QuickStartType]);
    const vacancyId = useSelector(selectQuickCampaignFormDataField[QuickCampaignFieldName.VacancyId]);

    const dispatch = useDispatch();
    const trl = useTrls();

    const onChangeSources = useCallback(
        (value: string, budget: number, budgetDaily: number) => {
            cpcQuickPublicationTypeButtonClick({
                vacancyId: Number(vacancyId),
                publicationType: value as PublicationType,
            });
            dispatch([
                setQuickCampaignFieldByName({ fieldName: QuickCampaignFieldName.QuickStartType, value }),
                setQuickCampaignFieldByName({
                    fieldName: QuickCampaignFieldName.TotalBudget,
                    value: budget,
                }),
                setQuickCampaignFieldByName({
                    fieldName: QuickCampaignFieldName.DailyBudget,
                    value: budgetDaily,
                }),
            ]);
        },
        [dispatch, vacancyId]
    );

    return (
        <MagritteFormField
            label={trl(TrlKeys.tariffLabel)}
            input={
                <div className={styles.container}>
                    {config.map(({ type, minBudget, minBudgetDaily }) => {
                        return (
                            <CheckableCard
                                padding={24}
                                borderRadius={24}
                                key={type}
                                type="radio"
                                value={type}
                                name="sources-item"
                                checked={type === quickStartType}
                                onChange={(event) => onChangeSources(event.target.value, minBudget, minBudgetDaily)}
                                data-qa={`campaign-type-${type}`}
                                flexible
                                toneHover
                            >
                                <div className={styles.content}>
                                    <VSpacingContainer default={8}>
                                        <div
                                            className={classnames(styles.icon, styles[`icon_${type.toLowerCase()}`])}
                                        />
                                        <Text typography="subtitle-1-semibold">{trl(`${TrlKeys[type]}.title`)}</Text>
                                        <Text typography="label-3-regular" style="secondary">
                                            {trl(`${TrlKeys[type]}.description`)}
                                        </Text>
                                    </VSpacingContainer>
                                    <Text typography="subtitle-1-semibold">
                                        {trl(TrlKeys.budget, [getFormattedIntNumber(Number(minBudget))])}
                                    </Text>
                                </div>
                            </CheckableCard>
                        );
                    })}
                </div>
            }
        />
    );
};
